import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://automatchlab.com';

function useFadeUp(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVisible(true), delay); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, visible };
}

const steps = {
  en: [
    { icon: 'ri-questionnaire-line', title: 'You Share Your Needs', desc: 'Answer a short set of smart questions about your lifestyle, usage patterns, budget, and preferences. No lengthy forms — focused and fast.' },
    { icon: 'ri-database-2-line', title: 'Our Platform Scans the Market', desc: 'Our system cross-references every make, model, trim, and price point currently available in Canada — thousands of data points processed instantly.' },
    { icon: 'ri-file-list-3-line', title: 'You Receive Your Shortlist', desc: 'A curated, expert-reviewed list of top vehicle matches arrives, each with a full set of specifications, fuel efficiency, EV range insights and price range.' },
  ],
  fr: [
    { icon: 'ri-questionnaire-line', title: 'Vous partagez vos besoins', desc: 'Répondez à quelques questions ciblées sur votre style de vie, vos habitudes, votre budget et vos préférences. Rapide et efficace.' },
    { icon: 'ri-database-2-line', title: 'Notre plateforme analyse le marché', desc: 'Notre système croise chaque marque, modèle, finition et gamme de prix disponibles au Canada — des milliers de points de données traités instantanément.' },
    { icon: 'ri-file-list-3-line', title: 'Vous recevez votre liste', desc: 'Une liste triée et validée par nos experts vous est remise — avec une explication complète pour chaque choix, les points d\'attention et des conseils d\'achat.' },
  ],
};



const stats = {
  en: [
    { value: '800+', label: 'Vehicles analyzed per query' },
    { value: '100%', label: 'Make & model coverage' },
    { value: '< 24h', label: 'Turnaround time' },
    { value: 'FREE', label: 'No cost, no commitment' },
  ],
  fr: [
    { value: '800+', label: 'Véhicules analysés par demande' },
    { value: '100%', label: 'Couverture marques & modèles' },
    { value: '< 24h', label: 'Délai de traitement' },
    { value: 'GRATUIT', label: 'Sans frais, sans engagement' },
  ],
};

function StepCard({ step, index, icon, title, desc }: { step: number; index: number; icon: string; title: string; desc: string }) {
  const { ref, visible } = useFadeUp(index * 100);
  return (
    <div ref={ref} className="flex gap-5" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)', transition: 'opacity 0.6s cubic-bezier(.22,1,.36,1), transform 0.6s cubic-bezier(.22,1,.36,1)' }}>
      <div className="flex flex-col items-center gap-2 flex-shrink-0">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm" style={{ background: 'rgba(196,168,130,0.12)', border: '1px solid rgba(196,168,130,0.25)', color: '#c4a882', fontFamily: 'Satoshi, Inter, sans-serif' }}>{step}</div>
        {index < 2 && <div className="flex-1 w-px" style={{ background: 'rgba(196,168,130,0.12)', minHeight: '32px' }}></div>}
      </div>
      <div className="pb-8">
        <div className="flex items-center gap-2 mb-2">
          <i className={`${icon} text-lg`} style={{ color: '#c4a882' }}></i>
          <h3 className="text-base sm:text-lg font-black text-white" style={{ fontFamily: 'Satoshi, Inter, sans-serif' }}>{title}</h3>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(196,168,130,0.55)' }}>{desc}</p>
      </div>
    </div>
  );
}

export default function MatchmakingPlatformPage() {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const heroFade = useFadeUp(0);
  const statsFade = useFadeUp(200);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const tx = {
    en: {
      navBack: 'Back',
      eyebrow: 'The Discovery — FREE',
      headline: 'Matchmaking Lab',
      sub: 'Powered by market-wide data and expert human review. Know exactly what to buy before you buy.',
      howTitle: 'How It Works',
      howSub: 'Three clear steps from your needs to your perfect vehicle shortlist.',
      ctaTitle: 'Ready to Find Your Vehicle?',
      ctaSub: 'No cost, no commitment. Start your matchmaking analysis and receive your personalized vehicle shortlist.',
      ctaBtn: 'Start Matchmaking Analysis',
      endToEnd: 'End-To-End Service',
    },
    fr: {
      navBack: 'Retour',
      eyebrow: 'La Découverte — GRATUIT',
      headline: "Notre Plateforme d'Analyse et de\u00a0Matchmaking",
      sub: 'Alimentée par des données couvrant tout le marché et validée par des experts humains. Sachez exactement quoi acheter.',
      howTitle: 'Comment ça fonctionne',
      howSub: 'Trois étapes claires de vos besoins à votre liste de véhicules idéaux.',
      ctaTitle: 'Prêt à trouver votre véhicule?',
      ctaSub: 'Sans frais, sans engagement. Commencez votre analyse et recevez votre liste de véhicules personnalisée.',
      ctaBtn: 'Commencer Gratuitement',
      endToEnd: 'Service Clé en Main',
    },
  }[lang];

  const seo = {
    en: {
      title: 'Matchmaking Analysis Platform – Automatchlab',
      description: 'Automatchlab\'s AI-powered vehicle matchmaking analysis platform. Scan every make and model in Canada and receive a personalized shortlist tailored to your needs, budget, and preferences.',
    },
    fr: {
      title: 'Plateforme d\'Analyse Matchmaking Véhicules – Automatchlab',
      description: 'La plateforme de matchmaking d\'Automatchlab. Analysez tous les modèles disponibles au Canada et recevez une liste personnalisée selon vos besoins, budget et préférences.',
    },
  }[lang];

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={`${siteUrl}/matchmaking-platform`} />
      <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'WebPage', name: 'Matchmaking Analysis Platform', url: `${siteUrl}/matchmaking-platform`, description: 'AI-powered vehicle analysis platform that matches buyers with their ideal vehicle across all makes, models, and price points.' })}</script>

      <div className="min-h-screen" style={{ background: '#06060e', fontFamily: 'Satoshi, Inter, sans-serif' }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 h-14 sm:h-16 flex items-center" style={{ background: 'rgba(6,6,14,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(196,168,130,0.12)' }}>
          <div className="w-full px-4 sm:px-8 lg:px-20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap" style={{ color: 'rgba(196,168,130,0.7)' }}>
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-left-line text-base"></i></div>
                {tx.navBack}
              </button>
              <div className="w-px h-4" style={{ background: 'rgba(196,168,130,0.2)' }}></div>
              <Link to="/"><img src="https://static.readdy.ai/image/b7d981aa05e2e3cec9e239b766e600d9/820f44dc42d7692bb1a0c21b796d9fab.svg" alt="Automatchlab" className="h-7 sm:h-9 w-auto object-contain" /></Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/end-to-end-service" className="hidden sm:flex items-center gap-1.5 text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer" style={{ color: 'rgba(196,168,130,0.6)' }}>
                <i className="ri-route-line"></i>{tx.endToEnd}
              </Link>
              <div className="flex items-center gap-0.5 rounded-lg p-0.5" style={{ background: 'rgba(196,168,130,0.08)', border: '1px solid rgba(196,168,130,0.15)' }}>
                <button onClick={() => setLang('en')} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${lang === 'en' ? 'text-[#0e0b07]' : 'text-[#c4a882]/60 hover:text-[#c4a882]'}`} style={lang === 'en' ? { background: '#c4a882' } : {}}>EN</button>
                <button onClick={() => setLang('fr')} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${lang === 'fr' ? 'text-[#0e0b07]' : 'text-[#c4a882]/60 hover:text-[#c4a882]'}`} style={lang === 'fr' ? { background: '#c4a882' } : {}}>FR</button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative overflow-hidden py-16 sm:py-24 lg:py-32 px-4 sm:px-8 lg:px-20">
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(196,168,130,0.05) 1px, transparent 1px)', backgroundSize: '36px 36px' }} />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(196,168,130,0.06) 0%, transparent 60%)' }} />
          <div ref={heroFade.ref} className="relative z-10 max-w-4xl mx-auto text-center" style={{ opacity: heroFade.visible ? 1 : 0, transform: heroFade.visible ? 'translateY(0)' : 'translateY(32px)', transition: 'opacity 0.8s cubic-bezier(.22,1,.36,1), transform 0.8s cubic-bezier(.22,1,.36,1)' }}>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5 sm:mb-7" style={{ letterSpacing: '-0.02em' }}>
              {tx.headline}
            </h1>
            <p className="text-base sm:text-xl leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-12" style={{ color: 'rgba(196,168,130,0.65)' }}>
              {tx.sub}
            </p>

            {/* Stats */}
            <div ref={statsFade.ref} className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-3xl mx-auto" style={{ opacity: statsFade.visible ? 1 : 0, transform: statsFade.visible ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.7s cubic-bezier(.22,1,.36,1) 0.2s, transform 0.7s cubic-bezier(.22,1,.36,1) 0.2s' }}>
              {stats[lang].map((s, i) => (
                <div key={i} className="rounded-2xl p-4 sm:p-5 text-center" style={{ background: 'rgba(196,168,130,0.05)', border: '1px solid rgba(196,168,130,0.12)' }}>
                  <div className="text-2xl sm:text-3xl font-black mb-1" style={{ color: '#c4a882' }}>{s.value}</div>
                  <div className="text-[10px] sm:text-xs leading-tight" style={{ color: 'rgba(196,168,130,0.45)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-14 sm:py-20 px-4 sm:px-8 lg:px-20" style={{ background: '#0c0c16' }}>
          <div className="max-w-3xl mx-auto">
            <div className="mb-10 sm:mb-14">
              <h2 className="text-2xl sm:text-4xl font-black text-white mb-3" style={{ letterSpacing: '-0.02em' }}>{tx.howTitle}</h2>
              <p className="text-sm sm:text-base" style={{ color: 'rgba(196,168,130,0.5)' }}>{tx.howSub}</p>
            </div>
            {steps[lang].map((step, i) => (
              <StepCard key={i} step={i + 1} index={i} icon={step.icon} title={step.title} desc={step.desc} />
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 px-4 sm:px-8 lg:px-20 text-center" style={{ background: '#0c0c16' }}>
          <div className="max-w-2xl mx-auto">
            <div className="h-px w-24 mx-auto mb-8" style={{ background: 'linear-gradient(90deg, transparent, #c4a882, transparent)' }}></div>
            <h2 className="text-2xl sm:text-4xl font-black text-white mb-4" style={{ letterSpacing: '-0.02em' }}>{tx.ctaTitle}</h2>
            <p className="text-sm sm:text-base mb-8 sm:mb-10" style={{ color: 'rgba(196,168,130,0.55)' }}>{tx.ctaSub}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="mailto:info@automatchlab.ca" className="flex items-center gap-2 px-8 py-4 rounded-xl font-black text-sm tracking-wide cursor-pointer whitespace-nowrap transition-all duration-200 text-[#0e0b07]" style={{ background: 'linear-gradient(135deg, #c4a882 0%, #a88c6a 100%)' }}>
                <i className="ri-mail-line"></i>
                {tx.ctaBtn}
              </a>

            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 sm:px-8 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(196,168,130,0.1)', background: '#06060e' }}>
          <Link to="/"><img src="https://static.readdy.ai/image/b7d981aa05e2e3cec9e239b766e600d9/820f44dc42d7692bb1a0c21b796d9fab.svg" alt="Automatchlab" className="h-7 w-auto object-contain" /></Link>
          <p className="text-xs" style={{ color: 'rgba(196,168,130,0.35)' }}>© 2024 Automatchlab. All rights reserved.</p>
        </footer>
      </div>
    </>
  );
}